import type { Snapshot } from "./types";

type SDK = typeof import("@amplitude/analytics-browser");
type Properties = Record<string, string | number | boolean>;
type EventName =
  | "Game Opened"
  | "Arena Ready"
  | "Arena Load Failed"
  | "Play Clicked"
  | "Room Entry Requested"
  | "Queue Joined"
  | "Room Joined"
  | "Match Entered"
  | "Combat Entered"
  | "Match Completed"
  | "Match Left"
  | "Invite Copied"
  | "Spell Learned"
  | "Spell Upgraded"
  | "Game Error";
interface PendingEvent {
  event_type: EventName;
  event_properties: Properties;
  time: number;
  insert_id?: string;
}

const DEDUP_KEY = "warlock.analytics.seen.v1";
const MAX_SEEN = 256;

/** Explicit product events only. Analytics must never hold up game startup. */
export class GameAnalytics {
  private sdk?: SDK;
  private initialization?: Promise<void>;
  private disabled = false;
  private pending: PendingEvent[] = [];
  private seen = new Set<string>();
  private lastSnapshot = "";

  start(): Promise<void> {
    return (this.initialization ??= this.initialize());
  }

  private async initialize(): Promise<void> {
    if (
      navigator.webdriver ||
      navigator.doNotTrack === "1" ||
      (navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl
    ) {
      this.disable();
      return;
    }
    try {
      const response = await fetch("/api/analytics-config", {
        signal: AbortSignal.timeout(3000),
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Analytics unavailable");
      const { apiKey } = await response.json();
      if (typeof apiKey !== "string" || !apiKey.trim()) {
        this.disable();
        return;
      }
      const sdk = await import("@amplitude/analytics-browser");
      await sdk.init(apiKey, {
        serverZone: "US",
        autocapture: false,
        remoteConfig: { fetchRemoteConfig: false },
        enableDiagnostics: false,
        identityStorage: "localStorage",
        trackingOptions: { ipAddress: false },
        flushIntervalMillis: 1000,
        flushQueueSize: 20,
      }).promise;
      this.sdk = sdk;
      for (const event of this.pending.splice(0)) this.send(event);
    } catch {
      // Blocked requests, storage restrictions, or SDK failures are non-fatal.
      this.disable();
    }
  }

  private disable(): void {
    this.disabled = true;
    this.pending = [];
  }

  track(name: EventName, properties: Properties = {}, insertId?: string): void {
    if (this.disabled) return;
    const event: PendingEvent = {
      event_type: name,
      event_properties: {
        ...properties,
        schema_version: 1,
        environment: ["localhost", "127.0.0.1", "[::1]"].includes(
          location.hostname,
        )
          ? "development"
          : "production",
      },
      time: Date.now(),
      ...(insertId ? { insert_id: insertId } : {}),
    };
    if (this.sdk) this.send(event);
    else if (this.pending.length < 32) this.pending.push(event);
  }

  private send(event: PendingEvent): void {
    try {
      void this.sdk?.track(event).promise.catch(() => {});
    } catch {
      /* Analytics cannot break the game. */
    }
  }

  flush(): void {
    try {
      void this.sdk?.flush().promise.catch(() => {});
    } catch {
      /* Best-effort on page exit. */
    }
  }

  private once(key: string, name: EventName, properties: Properties): void {
    if (this.disabled) return;
    // Session storage survives refresh/reconnect. Merge on each transition so
    // independent instances cannot forget already-observed milestones.
    try {
      const stored: unknown = JSON.parse(
        sessionStorage.getItem(DEDUP_KEY) ?? "[]",
      );
      if (Array.isArray(stored))
        for (const value of stored.slice(-MAX_SEEN))
          if (typeof value === "string") this.seen.add(value);
    } catch {
      /* In-memory dedup still works with unavailable storage. */
    }
    if (this.seen.has(key)) return;
    this.seen.add(key);
    while (this.seen.size > MAX_SEEN)
      this.seen.delete(this.seen.values().next().value!);
    try {
      sessionStorage.setItem(DEDUP_KEY, JSON.stringify([...this.seen]));
    } catch {}
    // Amplitude also deduplicates this ID across repeated delivery/tabs.
    this.track(name, properties, key);
  }

  queued(roomId: string): void {
    this.once(`queue:${roomId}`, "Queue Joined", {});
  }

  /** Call only after the active match's authoritative snapshot is accepted. */
  snapshot(
    matchId: string,
    playerId: number | null,
    snapshot: Snapshot,
    kind: string,
  ): void {
    if (this.disabled) return;
    const key = `${matchId}:${playerId ?? "observer"}`;
    const transition = `${key}:${snapshot.round}:${snapshot.phase}`;
    if (transition === this.lastSnapshot) return;
    this.lastSnapshot = transition;
    const properties: Properties = {
      match_id: matchId,
      role: playerId == null ? "observer" : "player",
      mode: snapshot.mode,
      room_kind: kind,
      round: snapshot.round,
      total_rounds: snapshot.total_rounds,
      players: snapshot.players.length,
      bots: snapshot.players.filter((player) => player.bot).length,
    };
    this.once(`${key}:entered`, "Match Entered", properties);
    if (snapshot.phase === "combat")
      this.once(
        `${key}:combat:${snapshot.round}`,
        "Combat Entered",
        properties,
      );
    if (snapshot.phase === "match_over") {
      const me = snapshot.players.find((player) => player.id === playerId);
      this.once(`${key}:completed`, "Match Completed", {
        ...properties,
        match_duration_seconds: Math.round(snapshot.tick / snapshot.tick_rate),
        ...(me
          ? {
              won: snapshot.winners.includes(me.id),
              eliminations: me.eliminations,
            }
          : {}),
      });
    }
  }
}

export const analytics = new GameAnalytics();
